import { ObservableField } from 'shared/utils/observer-field';
import {
  NativeMarkerResult,
  RecorderClock,
  RecorderConfig,
  RecorderConnection,
  RecorderDisconnectReason,
  RecorderPosition,
} from 'modules/video-markers/domain/interfaces';
import { parseVmixOperatorMarker, parseVmixState } from 'modules/video-markers/domain/utils';

const POLL_INTERVAL_MS = 5000;
/** Опрос, пока кто-то ждёт хоткей оператора: нажатие видно не позже чем через полсекунды. */
const OPERATOR_MARKER_POLL_INTERVAL_MS = 500;
const REQUEST_TIMEOUT_MS = 3000;

/** Web Controller vMix с логином и паролем («Settings → Web Controller → Password»). */
const HTTP_UNAUTHORIZED = 401;

export interface VmixRecorderClockOptions {
  /** Как часто проверять, что vMix на месте. Опрос и есть повтор подключения. */
  pollIntervalMs?: number;
  /** Как часто опрашивать vMix, пока подключён и есть подписчики `onOperatorMarker`. */
  operatorMarkerPollIntervalMs?: number;
  requestTimeoutMs?: number;
}

class VmixHttpError extends Error {
  constructor(readonly status: number) {
    super(`vMix ответил ${status}`);
  }
}

function errorToReason(error: unknown): RecorderDisconnectReason {
  if (error instanceof VmixHttpError) return error.status === HTTP_UNAUTHORIZED ? 'auth' : 'unknown';
  // `fetch` бросает `TypeError`, если vMix не запущен или Web Controller выключен; `AbortError` — таймаут.
  if (error instanceof TypeError || (error as Error | undefined)?.name === 'AbortError') return 'unreachable';
  return 'unknown';
}

/**
 * Адаптер порта `RecorderClock` для vMix через Web API (`http://127.0.0.1:8088/api`). Оверлей
 * открыт Web Browser input в том же vMix, поэтому `fetch` идёт на эту же машину; CORS-заголовок
 * (`Access-Control-Allow-Origin: *`) vMix отдаёт сам.
 *
 * Постоянного соединения у vMix нет: `connect` — первый запрос, дальше состояние проверяется
 * опросом раз в 5 с, он же служит повтором после обрыва. Позиция — только запись, с точностью до
 * секунды (`parseVmixState`).
 *
 * Хоткей оператора: шорткат vMix делает `LoopOn` служебному инпуту (`parseVmixOperatorMarker`).
 * Пока есть подписчики, опрос учащается до 500 мс; увиденный `loop="True"` — одна метка, и адаптер
 * тут же снимает его `LoopOff`. Два нажатия между опросами сливаются в одну метку.
 */
export class VmixRecorderClock implements RecorderClock {
  readonly kind = 'vmix' as const;
  readonly connection = new ObservableField<RecorderConnection>({ status: 'idle' });

  private isStopped = true;
  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly pollIntervalMs: number;
  private readonly operatorMarkerPollIntervalMs: number;
  private readonly requestTimeoutMs: number;
  private readonly operatorMarkerListeners = new Set<() => void>();
  /** Первый опрос после подключения: «взведённый» инпут — нажатие, когда нас не было, его время неизвестно. */
  private isOperatorMarkerBaseline = true;

  constructor(
    private readonly config: RecorderConfig,
    options: VmixRecorderClockOptions = {},
  ) {
    this.pollIntervalMs = options.pollIntervalMs ?? POLL_INTERVAL_MS;
    this.operatorMarkerPollIntervalMs = options.operatorMarkerPollIntervalMs ?? OPERATOR_MARKER_POLL_INTERVAL_MS;
    this.requestTimeoutMs = options.requestTimeoutMs ?? REQUEST_TIMEOUT_MS;
  }

  async connect(): Promise<void> {
    this.isStopped = false;
    this.isOperatorMarkerBaseline = true;
    this.clearPollTimer();
    this.connection.value = { status: 'connecting' };
    await this.poll();
  }

  disconnect(): void {
    this.isStopped = true;
    this.clearPollTimer();
    this.connection.value = { status: 'idle' };
  }

  async getPosition(): Promise<RecorderPosition> {
    return parseVmixState(await this.request(''));
  }

  /**
   * Строка в лог записи vMix (`Documents\vMixStorage\logs\<файл записи>.log`): время, позиция
   * записи с миллисекундами и `label`. Без записи vMix отвечает успехом, но ничего не пишет,
   * поэтому сначала проверяем, что запись идёт.
   */
  async writeNativeMarker(label: string): Promise<NativeMarkerResult> {
    try {
      const position = await this.getPosition();
      if (position.recordingMs === null) return 'unsupported';
      await this.request(`?Function=WriteDurationToRecordingLog&Value=${encodeURIComponent(label)}`);
      return 'written';
    } catch {
      return 'failed';
    }
  }

  onOperatorMarker(listener: () => void): () => void {
    this.operatorMarkerListeners.add(listener);
    // Опрос мог стоять на редком интервале — перейти на частый, не дожидаясь его.
    if (this.operatorMarkerListeners.size === 1 && this.connection.value.status === 'connected') this.schedulePoll();
    return () => {
      this.operatorMarkerListeners.delete(listener);
    };
  }

  private async poll(): Promise<void> {
    try {
      const xml = await this.request('');
      parseVmixState(xml);
      if (this.isStopped) return;
      this.setConnection({ status: 'connected' });
      await this.checkOperatorMarker(xml);
    } catch (error) {
      if (this.isStopped) return;
      this.setConnection({ status: 'disconnected', reason: errorToReason(error) });
      this.isOperatorMarkerBaseline = true;
    }
    if (this.isStopped) return;
    this.schedulePoll();
  }

  /** Опрос повторяет одно и то же состояние раз в 0,5–5 с — подписчиков будит только смена. */
  private setConnection(next: RecorderConnection) {
    const current = this.connection.value;
    const isSame =
      current.status === next.status &&
      (current.status !== 'disconnected' || next.status !== 'disconnected' || current.reason === next.reason);
    if (!isSame) this.connection.value = next;
  }

  private async checkOperatorMarker(xml: string): Promise<void> {
    const marker = parseVmixOperatorMarker(xml);
    const isBaseline = this.isOperatorMarkerBaseline;
    this.isOperatorMarkerBaseline = false;
    if (!marker?.isArmed) return;

    if (!isBaseline) this.operatorMarkerListeners.forEach((listener) => listener());
    await this.request(`?Function=LoopOff&Input=${encodeURIComponent(marker.key)}`);
  }

  private schedulePoll() {
    this.clearPollTimer();
    const isWatchingHotkey = this.operatorMarkerListeners.size > 0 && this.connection.value.status === 'connected';
    this.pollTimer = setTimeout(
      () => {
        this.pollTimer = null;
        void this.poll();
      },
      isWatchingHotkey ? this.operatorMarkerPollIntervalMs : this.pollIntervalMs,
    );
  }

  private clearPollTimer() {
    if (this.pollTimer !== null) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private async request(query: string): Promise<string> {
    const { host, port } = this.config;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), this.requestTimeoutMs);

    try {
      const response = await fetch(`http://${host}:${port}/api/${query}`, { signal: controller.signal });
      if (!response.ok) throw new VmixHttpError(response.status);
      return await response.text();
    } finally {
      clearTimeout(timeout);
    }
  }
}
